import { api } from 'store/api';
import { ApiResponse } from 'store/api/types';
import { TOKEN_ID_HEADER } from 'store/app/constants';
import {
  GenerateReconciliationDataResponse,
  ReconciliationHeaderData,
  ReconciliationQueryResponse,
  QueryFilters,
  ReconDataLog,
  SaveReconciliationReportParams,
  ViewReconciliationDataResponse,
} from './models';
import {
  AccountsFilter,
  MappingCodes,
  TaxRatesFilter,
} from 'store/vat/models/vatReportData';

// ── 1) Generate reconciliation report ──────────────────────────────────────

export interface GenerateReconciliationResponse extends ApiResponse {
  data: GenerateReconciliationDataResponse;
}

export const generateReconciliation = (
  connectionId: string,
  mappingId: string,
  toDate: string,
  accrualsPage: number,
  cashAccountingPage: number,
  reportId: string,
  currency?: string,
  pageSize?: number
): Promise<GenerateReconciliationResponse> => {
  const params = new URLSearchParams({
    toDate,
    accrualsPage: String(accrualsPage),
    cashAccountingPage: String(cashAccountingPage),
    reportId,
  });
  if (currency) params.set('currency', currency);
  if (pageSize) params.set('pageSize', String(pageSize));

  return api.get<GenerateReconciliationResponse>(
    `/auth/vat/reconciliation/generate/${connectionId}/mapping/${mappingId}?${params}`,
    {
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );
};

// ── 2) Query reconciliation results (paginated from cache) ─────────────────

export interface QueryReconciliationResponse extends ApiResponse {
  data: ReconciliationQueryResponse;
}

export const queryReconciliation = (
  reportId: string,
  page: number,
  pageSize: number,
  transactionType: string,
  groupingType: string,
  filters?: QueryFilters
): Promise<QueryReconciliationResponse> => {
  const url =
    `/auth/vat/query/${reportId}?page=${page}&pageSize=${pageSize}` +
    `&transactionType=${transactionType}&groupingType=${groupingType}`;
  const body = filters ? { body: filters } : {};
  return api.post<QueryReconciliationResponse>(url, body);
};

// ── 3) Load reconciliation header ──────────────────────────────────────────

export interface LoadReconciliationHeaderResponse extends ApiResponse {
  data: ReconciliationHeaderData;
}

export const loadReconciliationHeader = (
  connectionId: string,
  toDate: string
): Promise<LoadReconciliationHeaderResponse> =>
  api.get<LoadReconciliationHeaderResponse>(
    `/auth/vat/reconciliation/load/${connectionId}?toDate=${toDate}`,
    {
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );

// ── 4) View a previously saved reconciliation report ───────────────────────

export interface ViewReconciliationReportResponse extends ApiResponse {
  data: ViewReconciliationDataResponse;
}

export const viewReconciliationReport = (
  organisationId: string,
  dataLogId: string
): Promise<ViewReconciliationReportResponse> => {
  return api.get<ViewReconciliationReportResponse>(
    `/auth/vat/view/reconciliation/${organisationId}/log/${dataLogId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );
};

// ── 5) Load reconciliation transactions from saved log ─────────────────────

export interface LoadReconciliationTransactionsResponse extends ApiResponse {
  data: GenerateReconciliationDataResponse;
}

export interface LoadReconciliationTransactionsParams {
  accrualsPage?: number;
  cashAccountingPage?: number;
  pageSize?: number;
  reportId?: string;
}

export const loadReconciliationTransactions = (
  connectionId: string,
  mappingId: string,
  dataLogId: string,
  currency: string,
  accrualsPage: number,
  cashAccountingPage: number,
  reportId: string
): Promise<LoadReconciliationTransactionsResponse> => {
  const url =
    '/auth/vat/load/reconciliation/' +
    connectionId +
    '/mapping/' +
    mappingId +
    '/log/' +
    dataLogId +
    '?currency=' +
    currency +
    '&accrualsPage=' +
    accrualsPage +
    '&cashAccountingPage=' +
    cashAccountingPage +
    '&pageSize=15000' +
    '&reportId=' +
    reportId;

  return api.get<LoadReconciliationTransactionsResponse>(url, {
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });
};

// ── 6) Filters helpers ─────────────────────────────────────────────────────

export interface MappingCodesFilterResponse extends ApiResponse {
  data: MappingCodes;
}

export const getMappingCodesFilter = (
  mappingId: string
): Promise<MappingCodesFilterResponse> =>
  api.get<MappingCodesFilterResponse>(
    `/auth/vat/query/filter/mapping/${mappingId}`
  );

export interface AccountsFilterResponse extends ApiResponse {
  data: AccountsFilter;
}

export const getAccountsFilter = (
  connectionId: string
): Promise<AccountsFilterResponse> =>
  api.get<AccountsFilterResponse>(
    `/auth/vat/query/filter/accounts/${connectionId}`,
    {
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );

export interface TaxRatesFilterResponse extends ApiResponse {
  data: TaxRatesFilter;
}

export const getTaxRatesFilter = (
  connectionId: string
): Promise<TaxRatesFilterResponse> =>
  api.get<TaxRatesFilterResponse>(
    `/auth/vat/query/filter/taxRates/${connectionId}`,
    {
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );

// ── 7) Save reconciliation report ─────────────────────────────────────────
export interface SaveReconciliationReportResponse extends ApiResponse {}

export const saveReconciliationReport = (
  params: SaveReconciliationReportParams
): Promise<SaveReconciliationReportResponse> => {
  const {
    organisationId,
    mappingId,
    fromDate,
    toDate,
    generatedDate,
    currency,
    reportingTypeId,
    reportId,
    jsonStringsRequest,
  } = params;

  const query = new URLSearchParams({
    mappingId,
    fromDate,
    toDate,
    generatedDate,
    currency,
    reportingTypeId,
    reportId,
  });

  return api.post<SaveReconciliationReportResponse>(
    `/auth/status/save/reconciliation/${organisationId}?${query.toString()}`,
    {
      body: jsonStringsRequest,
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );
};

// ── 8) update Tax Balance ─────────────────────────────────────────────────
export const updateOrganisationTaxBalance = (
  organisationId: string,
  taxBalance: number
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/auth/organisation/${organisationId}/tax-balance?taxBalance=${taxBalance}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

// ── 9) Saved reconciliation logs for organisation ─────────────────────────
export interface ReconciliationLogsData {
  success: boolean;
  message: string;
  data: null;
  code: string | null;
  logs: ReconDataLog[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface ReconciliationLogsResponse extends ApiResponse {
  data: ReconciliationLogsData;
}

export const getReconciliationLogsForOrganisation = (
  organisationId: string,
  page = 1,
  pageSize = 10,
  filters?: Record<string, any>
): Promise<ReconciliationLogsResponse> => {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const generatedDateFilter = filters?.generatedDate?.value;
  if (
    Array.isArray(generatedDateFilter) &&
    generatedDateFilter.length === 2 &&
    generatedDateFilter[0] &&
    generatedDateFilter[1]
  ) {
    query.set('generatedDateFrom', String(generatedDateFilter[0]));
    query.set('generatedDateTo', String(generatedDateFilter[1]));
  }
  return api.get<ReconciliationLogsResponse>(
    `/auth/vat/reconciliation/logs/${organisationId}?${query.toString()}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );
};
